import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { CrudTab } from "./kit";
import { OverviewTab, SiteTab, BookTab, AudienceTab, BrandTab } from "./tabs";
import { DailyTab } from "./DailyTab";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "site", label: "Site" },
  { key: "book", label: "Book" },
  { key: "store", label: "Store" },
  { key: "brand", label: "Brand" },
  { key: "studio", label: "Studio" },
  { key: "daily", label: "Daily Chronicle" },
  { key: "series", label: "Series" },
  { key: "excerpts", label: "Excerpts" },
  { key: "quotes", label: "Quotes" },
  { key: "events", label: "Events" },
  { key: "audience", label: "Audience" },
];

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (location.state?.user) return;
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, [location.state]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    toast.success("Signed out");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center" data-testid="admin-loading">
        <p className="font-ui text-paper/50 text-xs uppercase tracking-[0.3em]">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-login">
        <div className="max-w-md w-full border border-paper/10 bg-coal p-10 text-center">
          <div className="flex justify-center mb-8"><Logo /></div>
          <h1 className="font-display text-2xl text-paper mb-3">Owner Dashboard</h1>
          <p className="font-ui text-sm text-paper/50 mb-8">
            Sign in with the authorized Google account to manage Dirty Church Chronicles.
          </p>
          <button onClick={login} data-testid="owner-google-login-btn"
            className="w-full font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-crimson text-paper px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimsonhi hover:scale-[1.02] active:scale-[0.98]">
            Sign in with Google
          </button>
          <Link to="/" className="block font-ui text-xs text-paper/40 mt-6 hover:text-paper transition-colors" data-testid="admin-back-home">
            ← Back to site
          </Link>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-denied">
        <div className="max-w-md w-full border border-paper/10 bg-coal p-10 text-center">
          <h1 className="font-display text-2xl text-paper mb-3">Not authorized</h1>
          <p className="font-ui text-sm text-paper/50 mb-8">
            {user.email} is not registered as the site owner. Sign in with the authorized Google account.
          </p>
          <button onClick={logout} data-testid="admin-denied-logout-btn"
            className="font-ui text-xs uppercase tracking-[0.25em] font-semibold border border-paper/25 text-paper px-8 py-4 rounded-full hover:border-paper/60 transition-colors duration-300">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="border-b border-paper/10 sticky top-0 bg-ink/90 backdrop-blur-md z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-5">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 font-ui text-xs text-paper/50 hover:text-paper transition-colors" data-testid="admin-view-site">
              View site <ExternalLink size={12} />
            </Link>
            <span className="hidden sm:block font-ui text-xs text-paper/40" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} aria-label="Sign out" data-testid="admin-logout-btn"
              className="p-2 text-paper/50 hover:text-crimsonhi transition-colors"><LogOut size={17} /></button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto pb-0" role="tablist" aria-label="Dashboard sections">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} role="tab" aria-selected={tab === t.key}
              data-testid={`admin-tab-btn-${t.key}`}
              className={`font-ui text-[11px] uppercase tracking-[0.18em] px-4 py-3 border-b-2 whitespace-nowrap transition-colors duration-300 ${
                tab === t.key ? "border-crimson text-paper" : "border-transparent text-paper/40 hover:text-paper/80"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {tab === "overview" && <OverviewTab />}
        {tab === "site" && <SiteTab />}
        {tab === "book" && <BookTab />}
        {tab === "brand" && <BrandTab />}
        {tab === "studio" && (
          <CrudTab name="videos" idField="video_id" title="Video Management Studio" orderable
            renderMeta={(x) => `${x.division || ""} · ${x.status || ""}${x.release_at ? " · " + String(x.release_at).slice(0, 16).replace("T", " ") : ""} · ${x.views || 0} views`}
            fields={[
              { key: "title", label: "Title" },
              { key: "slug", label: "URL slug (optional — auto-generated)", hint: "Letters, numbers, dashes. Changing it breaks existing links." },
              { key: "division", label: "Division", type: "select", options: [
                { value: "documentaries", label: "Documentaries" }, { value: "short-films", label: "Short Films" },
                { value: "feature-films", label: "Feature Films" }, { value: "original-series", label: "Original Series (episode)" },
                { value: "trailers", label: "Trailers" }, { value: "reels", label: "Reels (vertical)" },
                { value: "behind-the-scenes", label: "Behind the Scenes" }, { value: "studio-ident", label: "Studio Ident (opening sequence)" }] },
              { key: "logline", label: "Logline (one sentence)" },
              { key: "synopsis", label: "Synopsis / description", type: "textarea", rows: 4 },
              { key: "runtime", label: "Runtime", hint: "e.g. 24:10 or 1:42:00" },
              { key: "release_date", label: "Release date", type: "date" },
              { key: "genre", label: "Genre (optional)" },
              { key: "topic", label: "Topic / theme (optional)", hint: "e.g. Church accountability, repentance" },
              { key: "advisory", label: "Content advisory (optional)" },
              { key: "director", label: "Director" },
              { key: "producer", label: "Producer" },
              { key: "writer", label: "Writer" },
              { key: "cast", label: "Cast (optional)" },
              { key: "copyright_owner", label: "Copyright owner" },
              { key: "music_license", label: "Music licensing status", hint: "e.g. Original score — owned. Never upload unlicensed music." },
              { key: "footage_license", label: "Footage licensing status" },
              { key: "kind", label: "Source", type: "select", options: [{ value: "upload", label: "Uploaded video file" }, { value: "embed", label: "External embed URL" }] },
              { key: "url", label: "Video URL (embed) or uploaded file path" },
              { key: "url", label: "Upload video file (fills the Video URL field above)", type: "video" },
              { key: "poster_url", label: "Poster image (2:3)", type: "image" },
              { key: "backdrop_url", label: "Backdrop image (wide, optional)", type: "image" },
              { key: "captions_url", label: "Captions file (.vtt, optional)", hint: "Upload via the Upload button: .vtt files accepted." },
              { key: "trailer_slug", label: "Trailer slug (optional)", hint: "Slug of the trailer video for this production." },
              { key: "series_slug", label: "Series slug (episodes only)" },
              { key: "season", label: "Season number (episodes only)", type: "number" },
              { key: "episode", label: "Episode number (episodes only)", type: "number" },
              { key: "related_excerpt", label: "Related chronicle ID (optional)", hint: "Links 'Explore the chronicle that inspired this film'." },
              { key: "related_product", label: "Related product ID (optional)", hint: "e.g. bk-kindle-v1" },
              { key: "status", label: "Status", type: "select", options: [
                { value: "draft", label: "Draft (private)" },
                { value: "scheduled", label: "Scheduled premiere (countdown shown, unlocks at release time)" },
                { value: "published", label: "Published (public now)" },
                { value: "archived", label: "Archived (hidden everywhere)" }] },
              { key: "release_at", label: "Premiere date & time (for Scheduled)", type: "datetime" },
              { key: "timezone", label: "Premiere timezone", type: "select", options: [
                { value: "", label: "—" }, { value: "America/New_York", label: "Eastern (ET)" },
                { value: "America/Chicago", label: "Central (CT)" }, { value: "America/Denver", label: "Mountain (MT)" },
                { value: "America/Los_Angeles", label: "Pacific (PT)" }, { value: "UTC", label: "UTC" }] },
              { key: "placement_banner", label: "Placement: Featured Cinema banner", type: "bool" },
              { key: "placement_homepage", label: "Placement: Homepage", type: "bool" },
              { key: "placement_bookstore", label: "Placement: Bookstore", type: "bool" },
              { key: "placement_merch", label: "Placement: Merch store", type: "bool" },
              { key: "placement_chronicles", label: "Placement: Chronicle pages", type: "bool" },
              { key: "placement_author", label: "Placement: Author page", type: "bool" },
            ]} />
        )}
        {tab === "series" && (
          <CrudTab name="series" idField="series_id" title="Original Series" orderable
            renderMeta={(x) => x.slug || ""}
            fields={[
              { key: "title", label: "Series title" },
              { key: "slug", label: "URL slug (optional — auto-generated)" },
              { key: "logline", label: "Logline" },
              { key: "synopsis", label: "Synopsis", type: "textarea", rows: 4 },
              { key: "poster_url", label: "Series poster (2:3)", type: "image" },
              { key: "status", label: "Status", type: "select", options: [{ value: "draft", label: "Draft (private)" }, { value: "published", label: "Published (public)" }] },
            ]} />
        )}
        {tab === "excerpts" && (
          <CrudTab name="excerpts" idField="excerpt_id" title="Excerpts" orderable
            renderMeta={(x) => x.roman || ""}
            fields={[
              { key: "title", label: "Chronicle title" },
              { key: "roman", label: "Roman numeral (optional)", hint: "e.g. XII" },
              { key: "body", label: "Excerpt text", type: "textarea", rows: 12, hint: "Separate paragraphs with a blank line. Publish only text you have approved." },
              { key: "status", label: "Status", type: "select", options: [{ value: "draft", label: "Draft (private)" }, { value: "published", label: "Published (public)" }] },
            ]} />
        )}
        {tab === "quotes" && (
          <CrudTab name="quotes" idField="quote_id" title="Quotations" orderable
            renderMeta={(x) => x.source || ""}
            fields={[
              { key: "text", label: "Quotation", type: "textarea", rows: 4, hint: "Approved quotations only. These power the public quote-card generator." },
              { key: "source", label: "Source label (optional)", hint: "e.g. Chronicle XII" },
              { key: "status", label: "Status", type: "select", options: [{ value: "draft", label: "Draft (private)" }, { value: "published", label: "Published (public)" }] },
            ]} />
        )}
        {tab === "events" && (
          <CrudTab name="events" idField="event_id" title="Appearances"
            renderMeta={(x) => `${x.date || ""}${x.city ? " · " + x.city : ""}`}
            fields={[
              { key: "title", label: "Event title" },
              { key: "date", label: "Date", type: "date" },
              { key: "time", label: "Time (optional)", hint: "e.g. 6:30 PM" },
              { key: "timezone", label: "Time zone (optional)", hint: "e.g. CST" },
              { key: "venue", label: "Venue (optional)" },
              { key: "city", label: "City (optional)" },
              { key: "url", label: "Registration link (optional)" },
              { key: "description", label: "Description (optional)", type: "textarea", rows: 3 },
              { key: "status", label: "Status", type: "select", options: [{ value: "draft", label: "Draft (private)" }, { value: "published", label: "Published (public)" }] },
            ]} />
        )}
        {tab === "store" && (
          <div>
            <CrudTab name="products" idField="product_id" title="Products — Bookstore & Merch" orderable
              renderMeta={(x) => `${x.type || ""} · ${x.category || ""}${x.price ? " · $" + x.price : ""} · ${x.availability || ""}`}
              fields={[
                { key: "title", label: "Product title" },
                { key: "subtitle", label: "Subtitle / edition / collection line" },
                { key: "type", label: "Type", type: "select", options: [{ value: "book", label: "Book" }, { value: "merch", label: "Merchandise" }] },
                { key: "category", label: "Category", type: "select", options: [
                  { value: "ebook", label: "eBook" }, { value: "paperback", label: "Paperback" },
                  { value: "hardcover", label: "Hardcover" }, { value: "signed", label: "Signed edition" },
                  { value: "apparel", label: "Apparel" }, { value: "accessories", label: "Accessories" }] },
                { key: "collection", label: "Collection", type: "select", options: [
                  { value: "volume-one", label: "Volume One" }, { value: "signature", label: "Signature Collection" },
                  { value: "truth-that-confronts", label: "Truth That Confronts" },
                  { value: "author-collection", label: "Author Collection" }, { value: "limited", label: "Limited Editions" }] },
                { key: "description", label: "Description", type: "textarea", rows: 3 },
                { key: "specs", label: "Specifications (one per line)", type: "textarea", rows: 4 },
                { key: "price", label: "Price (numbers only, e.g. 24.99)", hint: "Shown publicly only when Price verified = yes." },
                { key: "price_verified", label: "Price verified", type: "select", options: [{ value: "no", label: "No — keep price private" }, { value: "yes", label: "Yes — display price" }] },
                { key: "availability", label: "Availability", type: "select", options: [
                  { value: "buy_external", label: "Buy externally (e.g. Amazon button)" },
                  { value: "coming_soon", label: "Coming Soon (notify-me form)" },
                  { value: "purchasable", label: "Purchasable (add to cart — use only when checkout is live)" },
                  { value: "hidden", label: "Hidden (not shown publicly)" }] },
                { key: "external_url", label: "External purchase URL (for Buy externally)" },
                { key: "external_label", label: "External button label", hint: "e.g. Buy on Amazon" },
                { key: "sizes", label: "Sizes (comma separated, optional)", hint: "e.g. S,M,L,XL,2XL" },
                { key: "badge", label: "Badge text", hint: "e.g. Available Now, Coming Soon, Limited" },
                { key: "image", label: "Product image (approved assets only)", type: "image" },
                { key: "fulfillment", label: "Fulfillment", type: "select", options: [
                  { value: "amazon", label: "Amazon KDP" }, { value: "lulu", label: "Lulu Direct (planned)" },
                  { value: "printful", label: "Printful (planned)" }, { value: "author", label: "Author-managed" },
                  { value: "none", label: "None / undecided" }] },
                { key: "status", label: "Status", type: "select", options: [{ value: "draft", label: "Draft (private)" }, { value: "published", label: "Published (public)" }] },
              ]} />
            <div className="mt-10 border border-paper/10 bg-coal p-8" data-testid="store-commerce-note">
              <h3 className="font-display text-xl text-paper mb-3">Commerce backend</h3>
              <p className="font-ui text-sm text-paper/55 leading-relaxed max-w-2xl">
                Orders, payments, taxes, discount codes, and fulfillment activate when Shopify is connected
                (owner approval pending). Printful (merch) and Lulu Direct (print books) are approved in principle
                and will route orders automatically after the Shopify connection. Until then, use this tab to
                manage products, prices, and visibility; the cart and checkout UI are built and waiting.
              </p>
            </div>
          </div>
        )}
        {tab === "audience" && <AudienceTab />}
        {tab === "daily" && <DailyTab />}
      </main>
    </div>
  );
}
